import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Truck, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/data/site";
import { sendInquiry } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";

const TYPE_IDS = ["food-manufacturer", "restaurant", "foodservice", "salsa-copacker", "distributor", "other"];
const INTEREST_IDS = ["colored-bell-peppers", "mexican-chilies", "cucumbers", "tomatoes", "squash-zucchini", "fresh-herbs", "salads-greens", "fresh-fruits", "iqf-products", "request-a-coa"];

const inputCls = "w-full rounded-xl border border-[var(--ink)]/15 bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-[var(--muted)] focus:border-[var(--green)]";

const EMPTY = { name: "", company: "", email: "", phone: "", business_type: "", interests: [], message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLang();
  const c = t.contact;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type) return toast.error(c.errType);
    if (form.message.trim().length < 10) return toast.error(c.errMsg);
    setLoading(true);
    try {
      await sendInquiry(form);
      toast.success(c.ok);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || c.fail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact Sebastian's Produce — Wholesale Produce Quotes, Los Angeles"
        description="Request a wholesale produce quote from Sebastian's Produce in Los Angeles. Email russel@sebastiansproduce.com or call 949-279-0791. Open Mon–Sat 4 AM–2 PM, delivering to LA, Orange County and the Inland Empire."
      />
      <PageHero key={lang} eyebrow={c.eyebrow} lines={c.lines} text={c.text} testId="contact-hero" />

      <section className="pb-24 lg:pb-32" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-[28px] border border-[var(--line)] bg-white p-7 shadow-[0_30px_60px_-40px_rgba(14,61,34,0.35)] lg:p-10" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block text-sm font-medium">{c.name}
                  <input required minLength={2} value={form.name} onChange={set("name")} className={`${inputCls} mt-2`} placeholder={c.namePh} data-testid="contact-input-name" />
                </label>
                <label className="block text-sm font-medium">{c.company}
                  <input required value={form.company} onChange={set("company")} className={`${inputCls} mt-2`} placeholder={c.companyPh} data-testid="contact-input-company" />
                </label>
                <label className="block text-sm font-medium">{c.email}
                  <input required type="email" value={form.email} onChange={set("email")} className={`${inputCls} mt-2`} placeholder="you@company.com" data-testid="contact-input-email" />
                </label>
                <label className="block text-sm font-medium">{c.phone} <span className="font-normal text-[var(--muted)]">{c.optional}</span>
                  <input value={form.phone} onChange={set("phone")} className={`${inputCls} mt-2`} placeholder="(555) 555-5555" data-testid="contact-input-phone" />
                </label>
              </div>

              <div className="mt-5">
                <p className="text-sm font-medium">{c.type}</p>
                <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
                  <SelectTrigger className="mt-2 h-auto rounded-xl border-[var(--ink)]/15 bg-white px-4 py-3 text-sm focus:ring-0 focus:border-[var(--green)]" data-testid="contact-select-business-type">
                    <SelectValue placeholder={c.typePh} />
                  </SelectTrigger>
                  <SelectContent>
                    {c.types.map((ty, i) => (
                      <SelectItem key={ty} value={ty} data-testid={`contact-business-type-option-${TYPE_IDS[i]}`}>{ty}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5">
                <p className="text-sm font-medium">{c.looking}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-interests">
                  {c.interests.map((i, idx) => {
                    const on = form.interests.includes(i);
                    return (
                      <button type="button" key={i} onClick={() => toggle(i)} data-testid={`contact-interest-${INTEREST_IDS[idx]}`} aria-pressed={on}
                        className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${on ? "border-[var(--green)] bg-[var(--green)] text-white" : "border-[var(--ink)]/15 hover:border-[var(--ink)]/40"}`}>
                        {i}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="mt-5 block text-sm font-medium">{c.message}
                <textarea required minLength={10} rows={5} value={form.message} onChange={set("message")} className={`${inputCls} mt-2 resize-y`} placeholder={c.messagePh} data-testid="contact-input-message" />
              </label>

              <button type="submit" disabled={loading} className="btn-primary mt-7 disabled:opacity-60" data-testid="contact-submit-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {loading ? c.sending : c.submit}
              </button>
              <p className="mt-4 text-xs text-[var(--muted)]">{c.note} {SITE.email}. {c.noSpam}</p>
            </form>
          </Reveal>

          <div className="space-y-4 lg:col-span-5">
            <Reveal delay={0.1} className="rounded-[28px] bg-[var(--green)] p-8 text-white" data-testid="contact-info-card">
              <p className="eyebrow !text-[var(--lime)]">{c.infoEyebrow}</p>
              <a href={`mailto:${SITE.email}`} className="font-display mt-5 block break-all text-xl font-bold leading-tight hover:underline lg:text-2xl" data-testid="contact-email-link">
                {SITE.email}
              </a>
              <a href={SITE.phoneHref} className="mt-3 inline-flex items-center gap-2 text-white/85 hover:text-white" data-testid="contact-phone-link">
                <Phone size={16} /> {SITE.phone}
              </a>
              <p className="mt-6 text-sm text-white/75">{c.infoText}</p>
            </Reveal>
            <Reveal delay={0.2} className="rounded-[28px] border border-[var(--line)] bg-white p-8" data-testid="contact-location-card">
              {[
                [MapPin, c.terminal, `${SITE.address}, ${SITE.city}`, SITE.mapsUrl],
                [Clock, c.hours, t.site.hours],
                [Truck, c.deliveries, `${t.site.delivery} · LA, Orange County, Inland Empire`],
                [Mail, c.response, c.responseVal],
              ].map(([Icon, h, s, href]) => (
                <div key={h} className="flex gap-4 border-b border-[var(--line)] py-4 first:pt-0 last:border-0 last:pb-0">
                  <Icon size={18} className="mt-0.5 shrink-0 text-[var(--green)]" />
                  <div>
                    <p className="font-mono-label text-[10px] text-[var(--muted)]">{h}</p>
                    {href ? <a href={href} target="_blank" rel="noreferrer" className="mt-1 block text-sm font-medium hover:text-[var(--green)]">{s}</a> : <p className="mt-1 text-sm font-medium">{s}</p>}
                  </div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
